import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, UserCheck, Database, Mail, Scale, Clock } from 'lucide-react';
import PageHeader from '../../components/UI/PageHeader';
import Container from '../../components/UI/Container';
import SEOHead from '../../components/SEO/SEOHead';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';

const PrivacyPolicy: React.FC = () => {
  const { settings } = useSystemSettings();

  const sections = [
    {
      icon: <UserCheck className="w-6 h-6" />,
      title: "1. Identitatea operatorului de date",
      content: `Operatorul datelor prelucrate prin intermediul acestui site este Sectorul 1 al Municipiului București - Primăria Sectorului 1, prin structurile sale de specialitate dedicate protecției animalelor.

Site-ul Protecția Animalelor Sector 1 este destinat informării publicului, promovării adopțiilor responsabile, publicării informațiilor despre animale și facilitării comunicării cu persoanele interesate de serviciile publice oferite.

Responsabilul cu protecția datelor poate fi contactat la adresa: dpo@primarias1.ro.`
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "2. Termeni utilizați",
      content: `Date cu caracter personal înseamnă orice informații privind o persoană fizică identificată sau identificabilă, inclusiv nume, date de contact, identificatori online sau alte elemente specifice identității sale.

Prelucrare înseamnă orice operațiune efectuată asupra datelor personale, cum ar fi colectarea, înregistrarea, stocarea, consultarea, utilizarea, transmiterea, restricționarea sau ștergerea.

Operator înseamnă entitatea care stabilește scopurile și mijloacele prelucrării datelor personale.`
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "3. Ce date putem prelucra",
      content: `În funcție de interacțiunea cu site-ul, putem prelucra următoarele categorii de date:
• Numele și prenumele dvs.
• Adresa de email
• Numărul de telefon
• Adresa sau localitatea, dacă este necesară pentru solicitări privind adopția sau sesizările transmise
• Informații furnizate în formulare de contact sau de adopție
• Date privind experiența cu animalele și condițiile declarate pentru adopție
• Documente sau fotografii transmise voluntar, dacă formularul folosit permite acest lucru
• Date tehnice limitate, precum adresa IP, tipul browserului, preferințele de accesibilitate sau acceptarea informării privind cookie-urile`
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "4. Scopurile prelucrării",
      content: `Datele sunt folosite pentru:
• Înregistrarea, analizarea și soluționarea solicitărilor transmise prin site
• Comunicarea cu persoanele interesate de adopții, sesizări sau informații publice
• Derularea procesului administrativ de adopție, acolo unde este cazul
• Verificarea compatibilității și a condițiilor declarate pentru adopție, în limitele permise de lege
• Gestionarea activității de contact și suport
• Îmbunătățirea funcționării site-ului și a accesibilității interfeței
• Respectarea obligațiilor legale ale autorității publice`
    },
    {
      icon: <Scale className="w-6 h-6" />,
      title: "5. Temeiul legal al prelucrării",
      content: `Prelucrarea datelor se poate realiza, după caz, în baza următoarelor temeiuri:
• Îndeplinirea unei sarcini care servește unui interes public sau exercitarea autorității publice
• Îndeplinirea unei obligații legale
• Interesul legitim pentru administrarea și securitatea site-ului
• Executarea demersurilor solicitate de persoana vizată înainte de încheierea unui act sau acord
• Consimțământul, atunci când legea îl cere pentru o operațiune specifică

În multe situații, pentru serviciile publice, temeiul principal nu este consimțământul, ci interesul public sau obligația legală.`
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "6. Destinatarii datelor",
      content: `Datele nu sunt vândute și nu sunt folosite pentru scopuri comerciale independente. Acestea pot fi comunicate, strict în limitele necesare, către:
• Structuri interne ale Primăriei Sectorului 1
• Personal sau colaboratori implicați în soluționarea solicitărilor privind animalele
• Medici veterinari sau servicii specializate, atunci când este necesar pentru sănătatea animalelor
• Furnizori tehnici care asigură găzduirea, mentenanța sau securitatea sistemelor
• Autorități publice competente, dacă există o obligație legală sau o solicitare întemeiată`
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "7. Durata păstrării datelor",
      content: `Datele sunt păstrate numai pe perioada necesară îndeplinirii scopurilor pentru care au fost colectate, cu respectarea termenelor legale de arhivare și a obligațiilor aplicabile autorităților publice.

Datele transmise pentru solicitări punctuale sunt păstrate pe durata soluționării cererii și ulterior conform regulilor de evidență, arhivare sau apărare a drepturilor și intereselor legitime ale instituției.`
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "8. Securitatea datelor",
      content: `Sunt aplicate măsuri tehnice și organizatorice pentru protejarea datelor, inclusiv acces restricționat, administrarea drepturilor de acces, măsuri de securitate informatică, monitorizarea funcționării sistemelor și instruirea persoanelor care prelucrează date personale.

Măsurile sunt adaptate naturii datelor, riscurilor identificate și obligațiilor prevăzute de RGPD.`
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "9. Drepturile persoanei vizate",
      content: `Conform RGPD, aveți următoarele drepturi:
• Dreptul de acces la datele dvs. personale
• Dreptul de rectificare a datelor incorecte
• Dreptul la ștergerea datelor, în condițiile legii
• Dreptul de restricționare a prelucrării
• Dreptul de portabilitate a datelor
• Dreptul de opoziție la prelucrare
• Dreptul de a nu face obiectul unei decizii bazate exclusiv pe prelucrare automată, inclusiv profilare
• Dreptul de retragere a consimțământului, atunci când prelucrarea se bazează pe consimțământ

Pentru exercitarea drepturilor, ne puteți contacta la dpo@primarias1.ro. De asemenea, vă puteți adresa Autorității Naționale de Supraveghere a Prelucrării Datelor cu Caracter Personal.`
    }
  ];

  return (
    <>
      <SEOHead 
        title="Politica de Confidențialitate - Protecția Animalelor Sector 1"
        description="Politica de confidențialitate pentru Adăpostul Odăi, București. Aflați cum protejăm și procesăm datele personale conform GDPR. Respectăm confidențialitatea adoptatorilor și donatorilor."
        keywords="politica confidentialitate adaspost, gdpr protectia animalelor, confidentialitate adopție caini, protecție date personale, politica privacitate adăpost"
        url="/politica-confidentialitate"
        type="website"
        noindex={true}
        image="https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />
      <PageHeader
        title="Politica de Confidențialitate"
        subtitle="Respectăm și protejăm confidențialitatea datelor dvs. personale"
        image="https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />

      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-blue-50 border-l-4 border-primary p-6 mb-8 rounded-r-lg">
            <div className="flex items-center mb-3">
              <Shield className="w-6 h-6 text-primary mr-3" />
              <h3 className="text-lg font-bold text-primary">Actualizare importantă</h3>
            </div>
            <p className="text-gray-700">
              Această politică de confidențialitate a fost actualizată la data de <strong>20 mai 2026</strong> și este aliniată structurii publicate de Primăria Sectorului 1 pentru protecția datelor cu caracter personal.
            </p>
          </div>

          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md p-6"
              >
                <div className="flex items-start mb-4">
                  <div className="bg-primary-100 p-3 rounded-full mr-4 flex-shrink-0">
                    <div className="text-primary">
                      {section.icon}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-primary mb-3 font-display">
                      {section.title}
                    </h2>
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 1.0 }}
            className="bg-white rounded-lg shadow-md p-6 mt-8"
          >
            <h2 className="text-xl font-bold text-primary mb-4 font-display">Contact pentru confidențialitate</h2>
            <p className="text-gray-700 mb-4">
              Pentru orice întrebări sau cereri legate de confidențialitate, ne puteți contacta:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>DPO:</strong> dpo@primarias1.ro</p>
              <p><strong>Email:</strong> {settings?.email || 'protectiaanimalelor@primarias1.ro'}</p>
              <p><strong>Telefon:</strong> {settings?.phone || '+40 750 209 751'}</p>
              <p><strong>Adresa:</strong> {settings?.address || 'Sos. Odăi, nr. 3-5, Sector 1, București'}</p>
            </div>
            <p className="text-gray-700 mt-4 text-sm">
              Ne angajăm să răspundem la toate solicitările dvs. în termen de 30 de zile calendaristice.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 1.2 }}
            className="bg-gray-50 rounded-lg p-6 mt-8"
          >
            <h2 className="text-xl font-bold text-primary mb-4 font-display">Modificări ale politicii</h2>
            <p className="text-gray-700">
              Ne rezervăm dreptul de a actualiza această politică de confidențialitate. Orice modificări vor fi publicate pe această pagină cu o nouă dată de actualizare. Vă încurajăm să revedeți periodic această politică pentru a fi la curent cu modul în care protejăm informațiile dvs.
            </p>
          </motion.div>
        </motion.div>
      </Container>
    </>
  );
};

export default PrivacyPolicy; 
